// src/pages/Exams/generate/TemplatesTab.tsx
import { useState } from 'react';
import { useExamTemplates } from './hooks/useExamTemplates';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import type { Blueprint, Template } from './types';
import { Badge, PagedFooter, PanelCard, Button, EmptyState, Spinner, SortableTh, Table, Td, Th } from '../../../school_panel/components/ui';
import { FaClipboardList } from '../../../school_panel/components/ui/icons';
import { useSortableTable, SortColumn } from '../../../school_panel/lib/useSortableTable';
import { usePagination } from '../../../school_panel/lib/usePagination';

interface Props {
  onUse: (bp: Blueprint) => void;
}

const COLUMNS: ReadonlyArray<SortColumn<Template>> = [
  { key: 'name',       accessor: (t) => t.name },
  { key: 'visibility', accessor: (t) => (t.is_shared ? 'Shared' : 'Private') },
  { key: 'updated',    accessor: (t) => t.updated_at, type: 'date' },
];

/** "30 questions · mixed · 6 chapters" (+ " · 2 forms" when more than one), read off the saved blueprint. */
export function templateMeta(bp: Blueprint | undefined): string {
  if (!bp) return '';
  const chapters = (bp.chapter_distribution ?? []).filter(c => c.count > 0).length;
  return [
    `${bp.total_questions} question${bp.total_questions === 1 ? '' : 's'}`,
    bp.difficulty_mode,
    `${chapters} chapter${chapters === 1 ? '' : 's'}`,
    bp.num_forms > 1 ? `${bp.num_forms} forms` : '',
  ].filter(Boolean).join(' · ');
}

export function TemplatesTab({ onUse }: Props) {
  const { templates, isLoading, renameTemplate, deleteTemplate, shareTemplate } = useExamTemplates();
  const [renaming, setRenaming] = useState<number | null>(null);
  const [draftName, setDraftName] = useState('');
  const { role } = useAuth();
  // Mirrors the backend rule at views/exam_generation.py:480-482, plus superuser.
  const canShare = role === 'school_admin' || role === 'admin' || role === 'superuser';
  const { rows, sort, toggle } = useSortableTable<Template>(
    templates, COLUMNS, { key: 'name', dir: 'asc' });
  const pg = usePagination<Template>(rows, 10, `${sort?.key}:${sort?.dir}`);

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<FaClipboardList />}
        title="No templates yet"
        body="Set up an exam on the Generate tab and choose “Save as template”."
      />
    );
  }

  return (
    <PanelCard title="Templates" subtitle="Save a set-up once, reuse it every term. Shared templates are visible to all teachers.">
    <Table minWidth={680}>
        <thead>
          <tr className="text-left">
            <SortableTh columnKey="name" sort={sort} onToggle={toggle} className="border-b border-line">Name</SortableTh>
            <SortableTh columnKey="visibility" sort={sort} onToggle={toggle} className="border-b border-line">Visibility</SortableTh>
            <SortableTh columnKey="updated" sort={sort} onToggle={toggle} className="border-b border-line">Updated</SortableTh>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {pg.pageItems.map((t: Template) => (
            <tr key={t.id}>
              <Td>
                {renaming === t.id ? (
                  <input
                    aria-label="Template name"
                    className="min-h-[36px] w-full rounded-[12px] border border-line-strong bg-surface px-2.5 py-1 text-sm text-ink focus:outline-2 focus:outline-offset-1 focus:outline-accent"
                    value={draftName} autoFocus
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && draftName.trim().length >= 1) {
                        renameTemplate(t.id, draftName.trim()); setRenaming(null);
                      }
                      if (e.key === 'Escape') setRenaming(null);
                    }} />
                ) : (
                  <>
                    <span className="block font-semibold text-ink">{t.name}</span>
                    {templateMeta(t.blueprint) && <span className="text-xs text-muted">{templateMeta(t.blueprint)}</span>}
                  </>
                )}
              </Td>
              <Td>
                <Badge tone={t.is_shared ? 'success' : 'info'}>
                  {t.is_shared ? 'Shared' : 'Private'}
                </Badge>
              </Td>
              <Td className="whitespace-nowrap font-mono text-[13px] text-muted">
                {new Date(t.updated_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
              </Td>
              <Td align="right" className="whitespace-nowrap">
                <div className="flex flex-wrap justify-end gap-1.5">
                  <Button variant="ghost" size="xs" onClick={() => onUse(t.blueprint)}>Use</Button>
                  <Button variant="ghost" size="xs"
                          onClick={() => { setRenaming(t.id); setDraftName(t.name); }}>Rename</Button>
                  {!t.is_shared && canShare && (
                    <Button variant="ghost" size="xs" onClick={() => shareTemplate(t.id)}>Share</Button>
                  )}
                  <Button variant="ghost" size="xs" className="!text-danger-ink"
                          onClick={() => deleteTemplate(t.id)}>Delete</Button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
    </Table>
    <PagedFooter className="mt-4" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Templates pagination" />
    </PanelCard>
  );
}
